import QRCode from "react-qr-code";
import {QrReader} from "react-qr-reader";
import React, {useCallback, useEffect, useLayoutEffect, useState} from "react";

export const Trilemma = ({isControl, receiveEvents, gameNumber, melee = false, finale = false}) => {
    if (isControl) return <TrilemmaControl gameNumber={gameNumber} receiveEvents={receiveEvents} melee={melee} finale={finale}/>
    return <TrilemmaPlayer gameNumber={gameNumber} receiveEvents={receiveEvents} melee={melee}/>;
}

// Animated trilemma backdrop with poster fallback. Uses position:fixed so the
// video stays anchored to the viewport while page content scrolls naturally.
// Poster image is also painted as a CSS background so it shows immediately
// while the video buffers (notably slow on iOS PWA standalone).
const TrilemmaBackdrop = ({darken = 0.45}) => {
    const videoRef = React.useRef(null);
    React.useEffect(() => {
        const v = videoRef.current;
        if (v) v.play().catch(() => {});
    }, []);
    return (
        <>
            <video
                ref={videoRef}
                src="/trilemma-assets/backdrop.mp4"
                poster="/trilemma-assets/backdrop.webp"
                autoPlay loop muted playsInline preload="auto"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    backgroundImage: 'url(/trilemma-assets/backdrop.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    zIndex: -2,
                    pointerEvents: 'none',
                }}
            />
            <div style={{
                position: 'fixed',
                inset: 0,
                backgroundImage: 'linear-gradient(rgba(10,20,50,0.25), #0a1530)',
                zIndex: -1,
                pointerEvents: 'none',
            }}/>
        </>
    );
};

const getEmoji = (moveType) => {
    switch (moveType.toLowerCase()) {
        case 'attack':
            return '⚔️';
        case 'reflect':
            return '🪞';
        case 'heal':
            return '🩹';
        default:
            return '';
    }
};

const getColor = (moveType) => {
    switch (moveType.toLowerCase()) {
        case 'attack':
            return '#ff4136';
        case 'reflect':
            return '#7fdbff';
        case 'heal':
            return '#2ecc40';
        default:
            return '#ffffff';
    }
};

const MeleeLadder = ({gameState}) => (
    <>
        <div style={{width: '100%', borderBottom: '1px solid white', marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, minHeight: 60, paddingBottom: 4}}>
            <img
                src="/trilemma-assets/logo.webp"
                alt="Trilemma"
                style={{display: 'block', height: 56, width: 'auto', filter: 'drop-shadow(2px 2px 0 rgba(0,0,0,0.8))'}}
            />
            <div className='digital' style={{fontSize: 44, lineHeight: 1}}>·</div>
            <div className='digital' style={{fontSize: 44, lineHeight: 1}}>{gameState.challengesClosed ? 'LAST CALL' : 'MELEE'}</div>
            {gameState.activeBattles > 0 &&
                <div className='digital' style={{fontSize: 30, opacity: 0.8, lineHeight: 1}}>· ⚔️ {gameState.activeBattles}</div>}
            {gameState.challengesClosed && gameState.activeBattles > 0 &&
                <div className='digital' style={{fontSize: 30, color: '#ff5a3a', lineHeight: 1}}>· FINAL BATTLES</div>}
        </div>
        <div style={{display: 'flex', width: '100%', flex: 1, minHeight: 0}}>
            <div style={{flex: 3, padding: '10px 16px'}}>
                {gameState.ladder.slice(0, 10).map((entry, i) => (
                    <div key={entry.character.token} style={{display: 'flex', alignItems: 'center', gap: 14, padding: '5px 10px', borderBottom: '1px solid rgba(255,255,255,0.15)'}}>
                        <div style={{fontSize: 34, fontWeight: 900, fontStyle: 'italic', width: 40, textAlign: 'center'}}>{i + 1}</div>
                        <img src={entry.character.image_url} alt={entry.character.name} style={{width: 62, height: 62, objectFit: 'cover', objectPosition: 'top'}}/>
                        <div style={{flex: 1, textAlign: 'left', fontSize: 30, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                            {entry.character.name}
                            {entry.inBattle && <span style={{fontSize: 22, marginLeft: 12}}>⚔️</span>}
                        </div>
                        <div style={{fontSize: 32, fontWeight: 900, width: 80, textAlign: 'right'}}>{entry.wins}W</div>
                        <div style={{fontSize: 24, opacity: 0.7, width: 60, textAlign: 'right'}}>{entry.losses}L</div>
                    </div>
                ))}
            </div>
            <div style={{flex: 2, padding: '10px 20px', borderLeft: '1px solid rgba(255,255,255,0.25)'}}>
                <div style={{fontSize: 24, fontWeight: 800, letterSpacing: 4, opacity: 0.75, textAlign: 'left', marginBottom: 10}}>LATEST BATTLES</div>
                {gameState.feed.map((f, i) => (
                    <div key={i} style={{display: 'flex', alignItems: 'center', gap: 12, padding: '7px 0', textAlign: 'left', opacity: Math.max(0.35, 1 - i * 0.12)}}>
                        <img src={f.winner.image_url} alt={f.winner.name} style={{width: 52, height: 52, objectFit: 'cover', objectPosition: 'top'}}/>
                        <div style={{fontSize: 25, lineHeight: 1.2}}>
                            <span style={{fontWeight: 800}}>{f.winner.name}</span>
                            <span style={{opacity: 0.7}}> defeated </span>
                            <span style={{fontWeight: 800}}>{f.loser.name}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </>
);

const TrilemmaControl = ({gameNumber, receiveEvents, melee, finale}) => {
    const [gameState, setGameState] = useState();
    const [challenger, setChallenger] = useState();
    const [challengee, setChallengee] = useState();
    const [isShowingLastTurn, setIsShowingLastTurn] = useState(false);
    const [isShowingBattleStart, setIsShowingBattleStart] = useState(false);
    const [lastDisplayedTurn, setLastDisplayedTurn] = useState();

    const updateGameState = useCallback(() => {
        if (melee) {
            fetch(`/trilemma/melee-board?gameNumber=${gameNumber}`, {credentials: 'include'})
                .then(response => response.json())
                .then(setGameState)
            return;
        }
        fetch('/trilemma/update-game-state', {
            credentials: 'include',
            method: 'post',
            body: JSON.stringify({gameNumber})
        }).then(response => response.json())
            .then(setGameState)
    }, []);
    useEffect(() => receiveEvents((d) => { if (d && d.update) updateGameState(); }), []);

    useEffect(() => {
        updateGameState();
    }, [])

    // Arriving at the finale scene starts the finale: the server does that (games/trilemma/finale_director.rb).

    useEffect(() => {
        if (!gameState || !gameState.finalBattleState) return;
        const fb = gameState.finalBattleState;
        if (lastDisplayedTurn === fb.turn) return;

        if (lastDisplayedTurn === undefined) {
            setLastDisplayedTurn(fb.turn);
            return;
        }

        if (!fb.lastTurn) {
            setIsShowingBattleStart(true);
            setLastDisplayedTurn(fb.turn);
            setTimeout(() => setIsShowingBattleStart(false), 2000);
        } else {
            setIsShowingLastTurn(true);
            setLastDisplayedTurn(fb.turn);
            setTimeout(() => setIsShowingLastTurn(false), fb.winner ? 5000 : 3000);
        }
    }, [gameState])

    const fakeBattle = () => {
        if(!challenger || !challengee) return;
        fetch(`/trilemma/fake-battle?gameNumber=${gameNumber}`, {
            credentials: 'include',
            method: 'post',
            body: JSON.stringify({gameNumber, challenger, challengee})
        }).then(() => {
                updateGameState();
                setChallenger('');
                setChallengee('');
            })
    }

    if(!gameState) return;

    const tournamentChampion = !melee &&
        gameState.remainingPlayers.length === 1 && gameState.winningPlayers.length === 0 &&
        gameState.eliminatedPlayers.length === 0 && gameState.inBattlePlayers.length === 0
        ? gameState.remainingPlayers[0] : null;
    const champion = melee ? gameState.champion : tournamentChampion;

    let battleView = null;
    if(champion) {
        battleView = <>
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{fontSize: 90, textShadow: '0 0 5px #ffffff, 0 0 5px #ffffff'}}>🎊🎊🎊</div>
                <div style={{fontSize: 80, textShadow: '0 0 5px #ffffff, 0 0 5px #ffffff'}}>🏆 CHAMPION 🏆</div>
                <div style={{
                    width: '100%',
                    paddingTop: 6,
                    paddingBottom: 6,
                    fontSize: 100,
                    display: 'flex',
                    textAlign: 'left',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <img style={{paddingRight: 40}} src={champion.image_url}
                         height={350}
                         width={350}/>
                    <div>
                        <div style={{fontWeight: 'bold'}}>{champion.name}</div>
                        <div style={{fontSize: 90}}>{champion.nickname}</div>
                    </div>
                </div>
                <div style={{fontSize: 90, marginTop: 20, textShadow: '0 0 5px #ffffff, 0 0 5px #ffffff'}}>🎊🎊🎊</div>
            </div>
        </>
    } else if (gameState.finalBattleState && !gameState.finalBattleState.winner) {
        const battle = gameState.finalBattleState;
        battleView = <div style={{width: '100%', height: '100%'}}>
            {gameState.isRedemption && <div style={{
                fontSize: 36,
                fontWeight: 900,
                fontStyle: 'italic',
                letterSpacing: 6,
                textTransform: 'uppercase',
                marginTop: 20,
                marginBottom: 20,
                color: '#ff5a3a',
                textShadow: '0 0 14px rgba(255,90,60,0.7), 2px 2px 0 rgba(0,0,0,0.95)',
                textAlign: 'center',
                width: '100%'
            }}>Redemption Battle
            </div>}
            <div style={{
                fontSize: 64,
                fontWeight: 800,
                letterSpacing: 6,
                textTransform: 'uppercase',
                color: '#f1d28b',
                textShadow: '0 0 18px rgba(232,177,90,0.6), 2px 2px 0 rgba(0,0,0,0.9)',
                marginTop: 20,
            }}>Turn {battle.turn}</div>
            <div style={{
                fontSize: 44,
                textAlign: 'center',
                fontWeight: 900,
                letterSpacing: 1,
                lineHeight: 1.4,
                marginBottom: 30,
            }}>
                {battle.lastTurn &&
                    !battle.lastTurn.challengerHealed &&
                    !battle.lastTurn.challengerDamaged &&
                    !battle.lastTurn.challengeeHealed &&
                    !battle.lastTurn.challengeeDamaged
                    && <div style={{color: '#ffffff', opacity: 0.9, textShadow: '0 0 14px rgba(0,0,0,0.95), 2px 2px 0 rgba(0,0,0,0.95)'}}>Nothing Happened</div>
                }
                {battle.lastTurn && battle.lastTurn.challengerHealed && <div style={{color: '#22cc3e', textShadow: '0 0 12px rgba(34,204,62,0.7), 2px 2px 0 rgba(0,0,0,0.95)'}}>{battle.challenger.name} Healed!</div>}
                {battle.lastTurn && battle.lastTurn.challengerDamaged && <div style={{color: '#ff5a3a', textShadow: '0 0 12px rgba(255,90,60,0.7), 2px 2px 0 rgba(0,0,0,0.95)'}}>{battle.challenger.name} Took Damage!</div>}
                {battle.lastTurn && battle.lastTurn.challengeeHealed && <div style={{color: '#22cc3e', textShadow: '0 0 12px rgba(34,204,62,0.7), 2px 2px 0 rgba(0,0,0,0.95)'}}>{battle.challengee.name} Healed!</div>}
                {battle.lastTurn && battle.lastTurn.challengeeDamaged && <div style={{color: '#ff5a3a', textShadow: '0 0 12px rgba(255,90,60,0.7), 2px 2px 0 rgba(0,0,0,0.95)'}}>{battle.challengee.name} Took Damage!</div>}
            </div>
            <div style={{
                display: 'flex',
                justifyContent: 'space-evenly',
                alignItems: 'center',
                width: '100%',
            }}>
                <div>
                    <img style={{
                        width: '350px',
                        objectFit: 'cover',
                        willChange: 'filter',
                        filter: (() => {
                            const outline = battle.challengerCritical ? '#5a0000' : 'black';
                            const stroke = `drop-shadow(0 0 2px ${outline}) drop-shadow(0 0 2px ${outline}) drop-shadow(0 0 2px ${outline})`;
                            return battle.challengerCritical
                                ? `${stroke} drop-shadow(0 0 30px rgba(214,54,28,0.9)) drop-shadow(0 0 60px rgba(214,54,28,0.6))`
                                : stroke;
                        })(),
                    }} src={battle.challenger.image_url}/>
                    <div style={{fontSize: 72, fontWeight: 800, lineHeight: 1.1, marginTop: 8, textShadow: '2px 2px 0 rgba(0,0,0,0.9)'}}>{battle.challenger.name}</div>
                    <div style={{fontSize: 36, fontStyle: 'italic', opacity: 0.9, lineHeight: 1.2, textShadow: '1px 1px 0 rgba(0,0,0,0.9)'}}>{battle.challenger.nickname}</div>
                    <div style={{
                        paddingTop: 16,
                        fontSize: 28,
                        fontWeight: 900,
                        fontStyle: 'italic',
                        letterSpacing: 4,
                        textTransform: 'uppercase',
                        color: battle.challengerCritical ? '#ff5a3a' : 'transparent',
                        textShadow: battle.challengerCritical ? '0 0 12px rgba(255,90,60,0.7), 2px 2px 0 rgba(0,0,0,0.95)' : 'none',
                    }}>Critical</div>
                </div>
                <div style={{
                    fontSize: 72,
                    fontWeight: 800,
                    letterSpacing: 6,
                    textTransform: 'uppercase',
                    opacity: 0.75,
                    textShadow: '2px 2px 0 rgba(0,0,0,0.9)',
                }}>vs.</div>
                <div>
                    <img style={{
                        width: '350px',
                        objectFit: 'cover',
                        willChange: 'filter',
                        filter: (() => {
                            const outline = battle.challengeeCritical ? '#5a0000' : 'black';
                            const stroke = `drop-shadow(0 0 2px ${outline}) drop-shadow(0 0 2px ${outline}) drop-shadow(0 0 2px ${outline})`;
                            return battle.challengeeCritical
                                ? `${stroke} drop-shadow(0 0 30px rgba(214,54,28,0.9)) drop-shadow(0 0 60px rgba(214,54,28,0.6))`
                                : stroke;
                        })(),
                    }} src={battle.challengee.image_url}/>
                    <div style={{fontSize: 72, fontWeight: 800, lineHeight: 1.1, marginTop: 8, textShadow: '2px 2px 0 rgba(0,0,0,0.9)'}}>{battle.challengee.name}</div>
                    <div style={{fontSize: 36, fontStyle: 'italic', opacity: 0.9, lineHeight: 1.2, textShadow: '1px 1px 0 rgba(0,0,0,0.9)'}}>{battle.challengee.nickname}</div>
                    <div style={{
                        paddingTop: 16,
                        fontSize: 28,
                        fontWeight: 900,
                        fontStyle: 'italic',
                        letterSpacing: 4,
                        textTransform: 'uppercase',
                        color: battle.challengeeCritical ? '#ff5a3a' : 'transparent',
                        textShadow: battle.challengeeCritical ? '0 0 12px rgba(255,90,60,0.7), 2px 2px 0 rgba(0,0,0,0.95)' : 'none',
                    }}>Critical</div>
                </div>
            </div>
        </div>

    } else if (melee && finale) {
        // Finale scene before the seed lands (duels still draining).
        battleView = <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 24,
            height: '100%',
        }}>
            <div style={{
                fontSize: 96,
                fontWeight: 900,
                fontStyle: 'italic',
                letterSpacing: 12,
                textTransform: 'uppercase',
                color: '#f1d28b',
                textShadow: '0 0 32px rgba(232,177,90,0.6), 4px 4px 0 rgba(0,0,0,0.95)',
            }}>The Finale</div>
            <div style={{fontSize: 34, fontWeight: 700, fontStyle: 'italic', opacity: 0.85}}>
                The finalists are being decided…
            </div>
        </div>;
    } else if (melee) {
        battleView = <MeleeLadder gameState={gameState}/>;
    } else if (gameState.isRedemption) {
        battleView = <>
            <div style={{
                fontSize: 48,
                fontWeight: 900,
                fontStyle: 'italic',
                letterSpacing: 6,
                textTransform: 'uppercase',
                marginTop: 20,
                marginBottom: 20,
                color: '#ff5a3a',
                textShadow: '0 0 14px rgba(255,90,60,0.7), 2px 2px 0 rgba(0,0,0,0.95)',
                textAlign: 'center',
                width: '100%'
            }}>Redemption Battle
            </div>
            <div style={{justifyContent: 'center', alignItems: 'center', display: 'flex', width: '100%'}}>
                {gameState.remainingPlayers.map((player, index) =>
                    <div key={index} style={{
                        width: '100%',
                        margin: '8px',
                        padding: '4px',
                        textAlign: 'center'
                    }}>
                        <div style={{fontSize: 38, marginBottom: 8}}>{player.name}</div>
                        <img style={{width: '150px'}} src={player.image_url}/>
                    </div>)
                }
            </div>
            <div style={{alignSelf: 'center', marginTop: 40, fontSize: 30}}>Choose</div>
            <div style={{justifyContent: 'center', display: 'flex', flexWrap: 'wrap', width: '100%'}}>
                {gameState.eliminatedPlayers.map((player, index) =>
                    <div key={index} style={{
                        width: '110px',
                        height: '120px',
                        margin: '8px',
                        padding: '4px',
                    }}>
                        <img style={{width: '100px', display: 'block', margin: 'auto'}} src={player.image_url}/>
                        <div style={{fontSize: 28}}>{player.name}</div>
                    </div>)
                }
            </div>
        </>
    } else {
        battleView = <>
            <div style={{width: '100%', borderBottom: '1px solid white', marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, minHeight: 60, paddingBottom: 4}}>
                <img
                    src="/trilemma-assets/logo.webp"
                    alt="Trilemma"
                    style={{display: 'block', height: 56, width: 'auto', filter: 'drop-shadow(2px 2px 0 rgba(0,0,0,0.8))'}}
                />
                <div className='digital' style={{fontSize: 44, lineHeight: 1}}>·</div>
                <div className='digital' style={{fontSize: 44, lineHeight: 1}}>Round {gameState.round}</div>
            </div>
            {gameState.winningPlayers.length > 0 &&
                <div style={{marginTop: 10, paddingLeft: 12, fontWeight: 'bold', marginBottom: '-10px', fontSize: 30}}>WINNERS</div>}
            <div style={{display: 'flex', flexWrap: 'wrap', width: '100%'}}>
                {gameState.winningPlayers.map((player, index) =>
                    <div key={index} style={{
                        width: '110px',
                        height: '120px',
                        margin: '8px',
                        padding: '4px',
                    }}>
                        <img style={{width: '100px', display: 'block', margin: 'auto'}} src={player.image_url}/>
                        <div style={{fontSize: 28}}>{player.name}</div>
                    </div>)
                }
            </div>
            {gameState.inBattlePlayers.length > 0 &&
                <div style={{marginTop: 12, fontWeight: 'bold', marginBottom: '-10px', paddingLeft: 16, fontSize: 30}}>IN BATTLE</div>}
            <div style={{display: 'flex', flexWrap: 'wrap', width: '100%'}}>
                {gameState.inBattlePlayers.map((player, index) =>
                    <div key={index} style={{
                        width: '110px',
                        height: '120px',
                        margin: '8px',
                        padding: '4px',
                    }}>
                        <img style={{width: '100px', display: 'block', margin: 'auto'}} src={player.image_url}/>
                        <div style={{fontSize: 28}}>{player.name}</div>
                    </div>)
                }
            </div>
            {gameState.remainingPlayers.length > 0 &&
                <div style={{marginTop: 12, fontWeight: 'bold', marginBottom: '-10px', paddingLeft: 16, fontSize: 30}}>REMAINING</div>}
            <div style={{display: 'flex', flexWrap: 'wrap', width: '100%'}}>
                {gameState.remainingPlayers.map((player, index) =>
                    <div key={index} style={{
                        width: '110px',
                        height: '120px',
                        margin: '8px',
                        padding: '4px',
                    }}>
                        <img style={{width: '100px', display: 'block', margin: 'auto'}} src={player.image_url}/>
                        <div style={{fontSize: 28}}>{player.name}</div>
                    </div>)
                }
            </div>
        </>

    }

    if(gameNumber === 0) {
        return <div className='digital' style={{
            height: '900px',
            width: '1500px',
            overflow: 'hidden',
            textAlign: 'center',
            alignSelf: 'flex-start',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 100,
        }}>
            WARM UP
        </div>
    }

    return <div style={{
        position: 'relative',
        height: '900px',
        width: '1500px',
        overflow: 'hidden',
        textAlign: 'center',
        alignSelf: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
    }}>
        <style>
            {`
          @keyframes enlarge {
            0% { transform: scale(100%); }
            100% { transform: scale(112%); }
          }
          @keyframes enlarge-delay {
            0% { transform: scale(100%); opacity: 0; }
            25% { opacity: 0; }
            35% { opacity: 1; }
            100% { transform: scale(150%); }
          }
          @keyframes slideLeft {
            0% { transform: translateX(-10%); opacity: 0; }
            10% { opacity: 1; }
            100% { transform: translateX(-25%); }
          }
          @keyframes slideRight {
            0% { transform: translateX(10%); opacity: 0; }
            10% { opacity: 0; }
            20% { opacity: 1; }
            100% { transform: translateX(25%); }
          }
        `}
        </style>
        <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10,
            pointerEvents: isShowingBattleStart ? 'auto' : 'none',
            backgroundColor: isShowingBattleStart ? 'black' : 'white',
            height: '900px',
            width: '1500px',
            opacity: isShowingBattleStart ? 1 : 0,
            transition: isShowingBattleStart ? 'none' : 'all 0.4s ease'
        }}>
            {isShowingBattleStart && <div style={{
                width: '100%',
                animation: 'enlarge 4s linear infinite',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}>
                <div style={{fontSize: 80}}>Battle Start!</div>
            </div>}
        </div>
        <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10,
            height: '900px',
            width: '1500px',
            pointerEvents: isShowingLastTurn ? 'auto' : 'none',
            backgroundColor: isShowingLastTurn ? 'black' : 'white',
            opacity: isShowingLastTurn ? 1 : 0,
            transition: isShowingLastTurn ? 'none' : 'all 0.4s ease'
        }}>
            {isShowingLastTurn && <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textShadow: `2px 2px 0 black, -2px -2px 0 black, 2px -2px 0 black, -2px 2px 0 black`,
            }}>
                <div style={{
                    animation: 'slideLeft 8s linear infinite',
                    color: 'white',
                    display: 'flex',
                    marginBottom: 30,
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <div style={{marginRight: 20, textAlign: 'center'}}>
                        <div style={{
                            fontSize: 44,
                            fontWeight: 800,
                            letterSpacing: 6,
                            textTransform: 'uppercase',
                            opacity: 0.75,
                            textShadow: '2px 2px 0 rgba(0,0,0,0.9)',
                            marginBottom: 8,
                        }}>{gameState.finalBattleState.challenger.name} used</div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 16,
                        }}>
                            <img
                                src={`/trilemma-assets/${moveIconKey(gameState.finalBattleState.lastTurn.challengerMove)}.png`}
                                alt={gameState.finalBattleState.lastTurn.challengerMove}
                                style={{
                                    width: 100,
                                    height: 100,
                                    objectFit: 'contain',
                                    filter: `drop-shadow(0 0 10px ${getColor(gameState.finalBattleState.lastTurn.challengerMove)}) drop-shadow(0 0 20px ${getColor(gameState.finalBattleState.lastTurn.challengerMove)}cc)`,
                                }}
                            />
                            <div style={{
                                fontSize: 80,
                                fontWeight: 'bold',
                                fontStyle: 'italic',
                                color: getColor(gameState.finalBattleState.lastTurn.challengerMove),
                                textShadow: `0 0 16px ${getColor(gameState.finalBattleState.lastTurn.challengerMove)}cc, 3px 3px 0 rgba(0,0,0,0.95)`,
                            }}>{gameState.finalBattleState.lastTurn.challengerMove}</div>
                        </div>
                    </div>
                    <div style={{textAlign: 'center', padding: 5}}>
                        <img src={gameState.finalBattleState.challenger.image_url} height={200} width={200}/>
                        <div style={{
                            fontSize: 32,
                            fontStyle: 'italic',
                            opacity: 0.9,
                            textShadow: '1px 1px 0 rgba(0,0,0,0.9)',
                            marginTop: 6,
                        }}>{gameState.finalBattleState.challenger.nickname}</div>
                    </div>
                </div>
                <div style={{
                    animation: 'slideRight 8s linear infinite',
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <div style={{textAlign: 'center', padding: 5}}>
                        <img src={gameState.finalBattleState.challengee.image_url} height={200} width={200}/>
                        <div style={{
                            fontSize: 32,
                            fontStyle: 'italic',
                            opacity: 0.9,
                            textShadow: '1px 1px 0 rgba(0,0,0,0.9)',
                            marginTop: 6,
                        }}>{gameState.finalBattleState.challengee.nickname}</div>
                    </div>
                    <div style={{marginLeft: 12, textAlign: 'center'}}>
                        <div style={{
                            fontSize: 44,
                            fontWeight: 800,
                            letterSpacing: 6,
                            textTransform: 'uppercase',
                            opacity: 0.75,
                            textShadow: '2px 2px 0 rgba(0,0,0,0.9)',
                            marginBottom: 8,
                        }}>{gameState.finalBattleState.challengee.name} used</div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 16,
                        }}>
                            <img
                                src={`/trilemma-assets/${moveIconKey(gameState.finalBattleState.lastTurn.challengeeMove)}.png`}
                                alt={gameState.finalBattleState.lastTurn.challengeeMove}
                                style={{
                                    width: 100,
                                    height: 100,
                                    objectFit: 'contain',
                                    filter: `drop-shadow(0 0 10px ${getColor(gameState.finalBattleState.lastTurn.challengeeMove)}) drop-shadow(0 0 20px ${getColor(gameState.finalBattleState.lastTurn.challengeeMove)}cc)`,
                                }}
                            />
                            <div style={{
                                fontSize: 80,
                                fontWeight: 'bold',
                                fontStyle: 'italic',
                                color: getColor(gameState.finalBattleState.lastTurn.challengeeMove),
                                textShadow: `0 0 16px ${getColor(gameState.finalBattleState.lastTurn.challengeeMove)}cc, 3px 3px 0 rgba(0,0,0,0.95)`,
                            }}>{gameState.finalBattleState.lastTurn.challengeeMove}</div>
                        </div>
                    </div>
                </div>
                {gameState.finalBattleState.winner && <div style={{
                    position: 'absolute',
                    inset: 0,
                    animation: 'enlarge-delay 8s linear infinite',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <div style={{
                        textAlign: 'center',
                        padding: 24,
                        fontSize: 140,
                        fontWeight: 900,
                        fontStyle: 'italic',
                        letterSpacing: 6,
                        textTransform: 'uppercase',
                        color: '#f1d28b',
                        textShadow: '0 0 30px rgba(232,177,90,0.85), 4px 4px 0 rgba(0,0,0,0.95)',
                    }}>
                        {gameState.finalBattleState.winner.name} wins!
                    </div>
                </div>}
            </div>
            }
        </div>
        {battleView}
        <div style={{
            position: 'fixed',
            zIndex: 5,
            width: '500px',
            bottom: 34,
            left: 800,
            right: 0,
            margin: 'auto'
        }}>
            <div style={{display: 'flex', }}>
                <input type="text" className="inputbox" placeholder={'Challenger'} value={challenger}
                       onChange={(event) => setChallenger(event.target.value)}/>
                <input type="text" className="inputbox" placeholder={'Challengee'} value={challengee}
                       onChange={(event) => setChallengee(event.target.value)}/>
                <button
                    style={{
                        border: '1px white solid',
                        padding: 10,
                        fontWeight: 400,
                        fontSize: 30,
                        color: 'white',
                        backgroundColor: 'black',
                    }}
                    onClick={fakeBattle}>Submit
                </button>
            </div>

        </div>
    </div>
}

const TrilemmaPlayer = ({gameNumber, receiveEvents, melee}) => {
    const [isScanning, setIsScanning] = useState(false);
    const [gameState, setGameState] = useState();
    const [isShowingBattleStart, setIsShowingBattleStart] = useState(false);
    const [isShowingLastTurn, setIsShowingLastTurn] = useState(false);
    const [lastDisplayedTurn, setLastDisplayedTurn] = useState();
    const [opponentToken, setOpponentToken] = useState();
    const [ackedBattleId, setAckedBattleId] = useState(null);
    const [scanError, setScanError] = useState(null);

    useEffect(() => receiveEvents((d) => { if (d && d.update) fetchGameState(); }), []);

    const fetchGameState = () => {
        const url = melee
            ? `/trilemma/melee-state?gameNumber=${gameNumber}`
            : `/trilemma/get-game-state-for-player?gameNumber=${gameNumber}`;
        fetch(url, {
            credentials: 'include'
        }).then(response => response.json())
            .then(setGameState)
    }

    useEffect(() => {
        fetchGameState();
    }, [])

    useLayoutEffect(() => {
        if (gameState && gameState['state'] === 'in-battle' && !gameState['lastTurn'] && !gameState['selectedMove'] && lastDisplayedTurn !== gameState['turn']) {
            setIsShowingBattleStart(true);
            setLastDisplayedTurn(gameState['turn']);
            setTimeout(() => setIsShowingBattleStart(false), 3000);
        } else if (gameState && gameState['lastTurn'] && !gameState['selectedMove'] && lastDisplayedTurn !== gameState['turn']) {
            setIsShowingLastTurn(true);
            setLastDisplayedTurn(gameState['turn']);
            setTimeout(() => setIsShowingLastTurn(false), 3000);
        }
    }, [gameState])

    const playAgain = () => {
        fetch('/trilemma/play-again', {
            method: 'post',
            credentials: 'include',
        }).then(() => fetchGameState())
    }

    const MELEE_ERRORS = {
        cannot_fight_yourself: "That's your own code.",
        unknown_opponent: "Couldn't find that player.",
        already_fought: 'Already fought them. Find someone new!',
        opponent_busy: "They're mid-battle. Try someone else.",
        finale_started: 'The melee is over!',
        melee_closed: 'Last call! No new challenges.',
    };

    const startBattle = ({opponentToken}) => {
        if (melee) {
            setScanError(null);
            fetch('/trilemma/melee/start-battle', {
                method: 'post',
                credentials: 'include',
                body: JSON.stringify({gameNumber, opponent_token: opponentToken})
            }).then(response => response.json())
                .then(result => {
                    if (result.error) setScanError(MELEE_ERRORS[result.error] || 'Challenge failed.');
                    fetchGameState();
                })
            return;
        }
        fetch('/trilemma/start-battle', {
            method: 'post',
            credentials: 'include',
            body: JSON.stringify({gameNumber, opponent_token: opponentToken})
        }).then(() => fetchGameState())
    }

    const performMove = ({move}) => {
        setGameState(prevState => ({...prevState, selectedMove: move}));
        fetch('/trilemma/perform-move', {
            method: 'post',
            credentials: 'include',
            body: JSON.stringify({gameNumber, move})
        }).then(() => fetchGameState())
    }

    if (!gameState) return;
    let battleView;

    const meleeResult = melee && gameState['state'] === 'start-battle' && gameState.lastResult &&
        gameState.lastResult.battleId !== ackedBattleId ? gameState.lastResult : null;

    const renderOutcome = ({headline, color, videoUrl, posterUrl, opponent = gameState.opponent, footer = null}) => <div style={{
        width: '100%',
        minHeight: '100dvh',
        boxSizing: 'border-box',
        padding: 'calc(40px + env(safe-area-inset-top)) 16px calc(32px + env(safe-area-inset-bottom))',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        color: 'white',
    }}>
        <video
            ref={(el) => el && el.play().catch(() => {})}
            src={videoUrl}
            poster={posterUrl}
            autoPlay loop muted playsInline preload="auto"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                backgroundImage: `url(${posterUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                zIndex: -2,
                pointerEvents: 'none',
            }}
        />
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.20)',
            zIndex: -1,
            pointerEvents: 'none',
        }}/>

        <div style={{
            fontSize: 56,
            fontWeight: 900,
            fontStyle: 'italic',
            letterSpacing: 4,
            textTransform: 'uppercase',
            textAlign: 'center',
            color,
            textShadow: `0 0 24px ${color}cc, 3px 3px 0 rgba(0,0,0,0.95)`,
            lineHeight: 1,
        }}>{headline}</div>

        <img
            src={opponent.image_url}
            alt={opponent.name}
            style={{
                display: 'block',
                width: 240,
                height: 240,
                objectFit: 'cover',
                willChange: 'filter',
                filter: 'drop-shadow(0 0 1px black) drop-shadow(0 0 1px black) drop-shadow(0 0 1px black)',
            }}
        />

        <div style={{textAlign: 'center'}}>
            <div style={{
                fontSize: 32,
                fontWeight: 800,
                lineHeight: 1.1,
                textShadow: '2px 2px 0 rgba(0,0,0,0.9)',
            }}>{opponent.name}</div>
            <div style={{
                fontSize: 20,
                fontStyle: 'italic',
                opacity: 0.9,
                textShadow: '1px 1px 0 rgba(0,0,0,0.9)',
            }}>{opponent.nickname}</div>
        </div>

        {gameNumber === 0 && <button
            onClick={playAgain}
            style={{
                padding: '18px 44px',
                border: '3px solid #e8b15a',
                borderRadius: 6,
                background: 'linear-gradient(180deg, #2a2520 0%, #14110d 100%)',
                color: '#f1d28b',
                fontSize: 28,
                fontWeight: 900,
                fontFamily: 'inherit',
                letterSpacing: 4,
                textTransform: 'uppercase',
                cursor: 'pointer',
                textShadow: '0 0 14px rgba(232,177,90,0.8), 2px 2px 0 rgba(0,0,0,0.95)',
                boxShadow: '0 0 22px rgba(232,177,90,0.5), inset 0 1px 0 rgba(255,220,160,0.3), 4px 4px 0 rgba(0,0,0,0.7)',
                marginTop: 12,
            }}
        >
            ▶ Play Again
        </button>}
        {footer}
    </div>;

    if (gameState['state'] === "winner") {
        battleView = renderOutcome({
            headline: 'You Defeated',
            color: '#f1d28b',
            videoUrl: '/trilemma-assets/victory.mp4',
            posterUrl: '/trilemma-assets/victory.webp',
        });
    }

    if (gameState['state'] === "champion") {
        battleView = <div style={{
            width: '100%',
            minHeight: '100dvh',
            boxSizing: 'border-box',
            padding: 'calc(40px + env(safe-area-inset-top)) 16px calc(32px + env(safe-area-inset-bottom))',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 24,
            color: 'white',
        }}>
            <video
                ref={(el) => el && el.play().catch(() => {})}
                src={'/trilemma-assets/victory.mp4'}
                poster={'/trilemma-assets/victory.webp'}
                autoPlay loop muted playsInline preload="auto"
                style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    width: '100%', height: '100%',
                    objectFit: 'cover',
                    zIndex: -2,
                    pointerEvents: 'none',
                }}
            />
            <div style={{position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.50)', zIndex: -1, pointerEvents: 'none'}}/>

            <div style={{
                fontSize: 32,
                fontWeight: 800,
                letterSpacing: 6,
                textTransform: 'uppercase',
                opacity: 0.85,
                textShadow: '2px 2px 0 rgba(0,0,0,0.9)',
            }}>You are the</div>

            <div style={{
                fontSize: 64,
                fontWeight: 900,
                fontStyle: 'italic',
                letterSpacing: 3,
                textTransform: 'uppercase',
                color: '#f1d28b',
                textShadow: '0 0 30px rgba(232,177,90,0.85), 4px 4px 0 rgba(0,0,0,0.95)',
                lineHeight: 1,
                textAlign: 'center',
            }}>Champion</div>

            <img
                src={gameState.player.image_url}
                alt={gameState.player.name}
                style={{
                    display: 'block',
                    width: 260,
                    height: 260,
                    objectFit: 'cover',
                    filter: 'drop-shadow(0 0 1px black) drop-shadow(0 0 1px black) drop-shadow(0 0 1px black) drop-shadow(0 0 30px rgba(232,177,90,0.7))',
                }}
            />

            <div style={{textAlign: 'center'}}>
                <div style={{
                    fontSize: 36,
                    fontWeight: 800,
                    lineHeight: 1.1,
                    textShadow: '2px 2px 0 rgba(0,0,0,0.9)',
                }}>{gameState.player.name}</div>
                <div style={{
                    fontSize: 22,
                    fontStyle: 'italic',
                    opacity: 0.9,
                    textShadow: '1px 1px 0 rgba(0,0,0,0.9)',
                }}>{gameState.player.nickname}</div>
            </div>
        </div>;
    }

    if (gameState['state'] === "eliminated") {
        battleView = renderOutcome({
            headline: 'Eliminated By',
            color: '#d6361c',
            videoUrl: '/trilemma-assets/defeat.mp4',
            posterUrl: '/trilemma-assets/defeat.webp',
        });
    }

    if (gameState['state'] === "in-battle") battleView = <div style={{
        width: '100%',
        minHeight: '100dvh',
        boxSizing: 'border-box',
        padding: gameState['critical']
            ? 'calc(20px + env(safe-area-inset-top)) 30px calc(32px + env(safe-area-inset-bottom))'
            : 'calc(12px + env(safe-area-inset-top)) 14px calc(16px + env(safe-area-inset-bottom))',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        position: 'relative',
        color: 'white',
    }}>
        {gameState['critical'] && (
            <div className="digital pulsing-critical" style={{
                alignSelf: 'center',
                marginBottom: 12,
                padding: '6px 18px',
                border: '3px solid #ff1a1a',
                borderRadius: 6,
                backgroundColor: 'rgba(60,0,0,0.85)',
                color: '#ff5a3a',
                fontSize: 18,
                fontWeight: 800,
                fontStyle: 'italic',
                letterSpacing: 3,
                boxShadow: '0 0 16px rgba(255,26,26,0.5), 4px 4px 0 rgba(0,0,0,0.7)',
                animation: 'pulse 2s infinite',
            }}>
                CRITICAL
            </div>
        )}

        <div style={{
            alignSelf: 'center',
            fontSize: 18,
            fontWeight: 800,
            letterSpacing: 3,
            textTransform: 'uppercase',
            color: '#f1d28b',
            textShadow: '0 0 10px rgba(232,177,90,0.5), 1px 1px 0 rgba(0,0,0,0.9)',
        }}>
            Turn {gameState['turn']}
        </div>

        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            padding: '4px 4px',
            width: 'fit-content',
            margin: '0 auto',
            maxWidth: '100%',
        }}>
            <div style={{fontSize: 26, fontWeight: 800, opacity: 0.75, textShadow: '2px 2px 0 rgba(0,0,0,0.9)'}}>vs.</div>
            <img
                style={{
                    width: 96,
                    height: 96,
                    objectFit: 'cover',
                    willChange: 'filter',
                    filter: (() => {
                        const outline = gameState.opponentCritical ? '#5a0000' : 'black';
                        // 3 stacked tight-radius blurs ≈ a hard outline, but
                        // way cheaper than 8 directional drop-shadows.
                        const stroke = `drop-shadow(0 0 1px ${outline}) drop-shadow(0 0 1px ${outline}) drop-shadow(0 0 1px ${outline})`;
                        return gameState.opponentCritical
                            ? `${stroke} drop-shadow(0 0 12px rgba(214,54,28,0.85))`
                            : stroke;
                    })(),
                }}
                src={gameState.opponent.image_url}
                alt={gameState.opponent.name}
            />
            <div style={{minWidth: 0}}>
                {gameState.opponentCritical && (
                    <div className={'digital'} style={{
                        fontSize: 13,
                        color: '#ff5a3a',
                        letterSpacing: 2,
                        marginBottom: 2,
                        textShadow: '0 0 10px rgba(255,90,60,0.7), 1px 1px 0 rgba(0,0,0,0.9)',
                    }}>
                        CRITICAL
                    </div>
                )}
                <div style={{
                    fontWeight: 800,
                    fontSize: 24,
                    lineHeight: 1.1,
                    textShadow: '2px 2px 0 rgba(0,0,0,0.9)',
                }}>{gameState.opponent.name}</div>
                <div style={{
                    fontSize: 17,
                    opacity: 0.9,
                    fontStyle: 'italic',
                    lineHeight: 1.2,
                    textShadow: '1px 1px 0 rgba(0,0,0,0.9)',
                }}>{gameState.opponent.nickname}</div>
            </div>
        </div>

        <div style={{
            textAlign: 'center',
            fontWeight: 900,
            fontSize: 24,
            lineHeight: 1.4,
            letterSpacing: 1,
        }}>
            {(() => {
                const lineStyle = (color) => ({
                    color,
                    textShadow: `0 0 12px ${color}aa, 2px 2px 0 rgba(0,0,0,0.95)`,
                });
                return <>
                    {gameState['lastTurn'] &&
                        !gameState['lastTurn']['playerHealed'] &&
                        !gameState['lastTurn']['playerDamaged'] &&
                        !gameState['lastTurn']['opponentHealed'] &&
                        !gameState['lastTurn']['opponentDamaged']
                        && <div style={{
                            color: '#ffffff',
                            opacity: 0.9,
                            textShadow: '0 0 14px rgba(0,0,0,0.95), 2px 2px 0 rgba(0,0,0,0.95)',
                        }}>Nothing Happened</div>
                    }
                    {gameState['lastTurn'] && gameState['opponentCritical'] && <div style={lineStyle('#22cc3e')}>Opponent Critical!</div>}
                    {gameState['lastTurn'] && gameState['lastTurn']['playerHealed'] && <div style={lineStyle('#22cc3e')}>You Healed!</div>}
                    {gameState['lastTurn'] && gameState['lastTurn']['playerDamaged'] && <div style={lineStyle('#ff5a3a')}>You Took Damage!</div>}
                    {gameState['lastTurn'] && gameState['lastTurn']['opponentHealed'] && <div style={lineStyle('#ff5a3a')}>Opponent Healed!</div>}
                </>;
            })()}
        </div>

        <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            gap: 12,
        }}>
            <div style={{
                textAlign: 'center',
                fontSize: 14,
                fontWeight: 800,
                letterSpacing: 4,
                textTransform: 'uppercase',
                opacity: 0.75,
                marginBottom: 4,
            }}>
                {gameState['selectedMove'] ? 'You Chose' : 'Choose Move'}
            </div>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                width: 'fit-content',
                margin: '0 auto',
            }}>
                {!gameState['selectedMove'] && <>
                    <MoveButton move={'Attack'} performMove={performMove}/>
                    <MoveButton move={'Reflect'} performMove={performMove}/>
                    <MoveButton move={'Heal'} performMove={performMove}/>
                </>}
                {gameState['selectedMove'] && <>
                    <MoveButton move={'Attack'} hidden={gameState['selectedMove'] !== 'ATTACK'} selected={gameState['selectedMove'] === 'ATTACK'} performMove={() => {}}/>
                    <MoveButton move={'Reflect'} hidden={gameState['selectedMove'] !== 'REFLECT'} selected={gameState['selectedMove'] === 'REFLECT'} performMove={() => {}}/>
                    <MoveButton move={'Heal'} hidden={gameState['selectedMove'] !== 'HEAL'} selected={gameState['selectedMove'] === 'HEAL'} performMove={() => {}}/>
                </>}
            </div>
        </div>
    </div>

    if (meleeResult) battleView = renderOutcome({
        headline: meleeResult.won ? 'You Defeated' : 'Defeated By',
        color: meleeResult.won ? '#f1d28b' : '#ff5a3a',
        videoUrl: meleeResult.won ? '/trilemma-assets/victory.mp4' : '/trilemma-assets/defeat.mp4',
        posterUrl: meleeResult.won ? '/trilemma-assets/victory.webp' : '/trilemma-assets/defeat.webp',
        opponent: meleeResult.opponent,
        footer: <button
            onClick={() => setAckedBattleId(meleeResult.battleId)}
            style={{
                padding: '18px 44px',
                border: '3px solid #e8b15a',
                borderRadius: 6,
                background: 'linear-gradient(180deg, #2a2520 0%, #14110d 100%)',
                color: '#f1d28b',
                fontSize: 28,
                fontWeight: 900,
                fontFamily: 'inherit',
                letterSpacing: 4,
                textTransform: 'uppercase',
                cursor: 'pointer',
                textShadow: '0 0 14px rgba(232,177,90,0.8), 2px 2px 0 rgba(0,0,0,0.95)',
                boxShadow: '0 0 22px rgba(232,177,90,0.5), inset 0 1px 0 rgba(255,220,160,0.3), 4px 4px 0 rgba(0,0,0,0.7)',
                marginTop: 12,
            }}
        >
            ▶ Next Opponent
        </button>,
    });

    if (gameState['state'] === 'spectator') {
        const finale = gameState.finale;
        battleView = <div style={{
            width: '100%',
            minHeight: '100dvh',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 20,
            textAlign: 'center',
            color: 'white',
            padding: 16,
        }}>
            <div style={{fontSize: 44, fontWeight: 900, fontStyle: 'italic', letterSpacing: 4, textTransform: 'uppercase', color: '#f1d28b', textShadow: '0 0 18px rgba(232,177,90,0.6), 2px 2px 0 rgba(0,0,0,0.9)'}}>Finale</div>
            <div style={{display: 'flex', alignItems: 'center', gap: 18}}>
                <div>
                    <img src={finale.challenger.image_url} alt={finale.challenger.name} style={{width: 130, height: 130, objectFit: 'cover'}}/>
                    <div style={{fontSize: 22, fontWeight: 800}}>{finale.challenger.name}</div>
                </div>
                <div style={{fontSize: 30, fontWeight: 800, opacity: 0.75}}>vs.</div>
                <div>
                    <img src={finale.challengee.image_url} alt={finale.challengee.name} style={{width: 130, height: 130, objectFit: 'cover'}}/>
                    <div style={{fontSize: 22, fontWeight: 800}}>{finale.challengee.name}</div>
                </div>
            </div>
            <div style={{fontSize: 24, fontWeight: 700, opacity: 0.9}}>
                {finale.winner ? `👑 ${finale.winner.name} takes the crown!` : 'Watch the big screen!'}
            </div>
            <div style={{fontSize: 17, opacity: 0.7}}>Your melee record is locked in: {gameState.wins} wins</div>
        </div>;
    }

    if (!meleeResult && gameState['state'] === "start-battle") battleView = <div style={{
        width: '100%',
        minHeight: '100dvh',
        boxSizing: 'border-box',
        padding: 'calc(16px + env(safe-area-inset-top)) 16px calc(24px + env(safe-area-inset-bottom))',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        position: 'relative',
        color: 'white',
    }}>
        <img
            src="/trilemma-assets/logo.webp"
            alt="Trilemma"
            style={{
                display: 'block',
                width: '80%',
                maxWidth: 360,
                margin: '0 auto',
                filter: 'drop-shadow(3px 3px 0 rgba(0,0,0,0.8))',
            }}
        />
        {gameNumber !== 0 && (melee
            ? <div style={{
                alignSelf: 'center',
                textAlign: 'center',
                fontSize: 20,
                fontWeight: 800,
                letterSpacing: 3,
                textTransform: 'uppercase',
                textShadow: '2px 2px 0 rgba(0,0,0,0.8)',
            }}>One Battle Per Opponent</div>
            : <div style={{
                alignSelf: 'center',
                textAlign: 'center',
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: 2,
                textTransform: 'uppercase',
                textShadow: '2px 2px 0 rgba(0,0,0,0.8)',
            }}>
                Round {gameState['round']}
            </div>
        )}

        {melee && gameState.challengesClosed && <div style={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, textAlign: 'center'}}>
            <div style={{fontSize: 40, fontWeight: 900, fontStyle: 'italic', letterSpacing: 4, textTransform: 'uppercase', color: '#ff5a3a', textShadow: '0 0 16px rgba(255,90,60,0.7), 2px 2px 0 rgba(0,0,0,0.95)'}}>Last Call</div>
            <div style={{fontSize: 22, fontWeight: 700, opacity: 0.9, textShadow: '1px 1px 0 rgba(0,0,0,0.9)'}}>No new challenges.<br/>The finale approaches…</div>
        </div>}

        {!(melee && gameState.challengesClosed) && <div style={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
            <div style={{
                width: 256,
                height: 256,
                padding: 8,
                border: '3px solid white',
                borderRadius: 6,
                backgroundColor: isScanning ? 'black' : 'white',
                boxShadow: '4px 4px 0 rgba(0,0,0,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
            }}>
                {isScanning ? (
                    <QrReader
                        onResult={(result, error) => {
                            if (!!result && result.text) {
                                setIsScanning(false);
                                setGameState({});
                                startBattle({opponentToken: result.text});
                            }
                            if (!!error) console.info(error);
                        }}
                        containerStyle={{width: '100%', height: '100%'}}
                        constraints={{facingMode: 'environment'}}
                    />
                ) : (
                    <QRCode
                        style={{height: "100%", width: "100%"}}
                        value={gameState.token}
                        viewBox={`0 0 256 256`}
                    />
                )}
            </div>
        </div>}


        {melee && scanError && <div style={{textAlign: 'center', color: '#ff8a72', fontSize: 20, fontWeight: 700, textShadow: '1px 1px 0 rgba(0,0,0,0.9)'}}>{scanError}</div>}
        {melee && gameState.history.length > 0 && <div style={{textAlign: 'center', paddingBottom: 8}}>
            <div style={{fontSize: 13, fontWeight: 800, letterSpacing: 4, textTransform: 'uppercase', opacity: 0.65, marginBottom: 6}}>
                Already Fought
            </div>
            <div style={{display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 6}}>
                {gameState.history.map((b, i) => (
                    <span key={i} style={{
                        fontSize: 16,
                        fontWeight: 700,
                        padding: '4px 10px',
                        borderRadius: 12,
                        border: `1px solid ${b.won ? 'rgba(46,204,64,0.8)' : 'rgba(255,90,60,0.8)'}`,
                        color: b.won ? '#7dff96' : '#ff9d8a',
                        textShadow: '1px 1px 0 rgba(0,0,0,0.9)',
                    }}>
                        {b.won ? '✓' : '✗'} {b.opponent}
                    </span>
                ))}
            </div>
        </div>}
        {!(melee && gameState.challengesClosed) && <button
            onClick={() => setIsScanning(!isScanning)}
            style={{
                width: '100%',
                padding: '20px 0',
                border: isScanning ? '3px solid #ff5a3a' : '3px solid #e8b15a',
                borderRadius: 6,
                background: isScanning
                    ? 'linear-gradient(180deg, #2a1512 0%, #140b09 100%)'
                    : 'linear-gradient(180deg, #2a2520 0%, #14110d 100%)',
                color: isScanning ? '#ff8a72' : '#f1d28b',
                fontSize: 32,
                fontWeight: 900,
                fontFamily: 'inherit',
                letterSpacing: 4,
                textTransform: 'uppercase',
                cursor: 'pointer',
                textShadow: isScanning
                    ? '0 0 16px rgba(255,90,60,0.8), 2px 2px 0 rgba(0,0,0,0.95)'
                    : '0 0 16px rgba(232,177,90,0.7), 2px 2px 0 rgba(0,0,0,0.95)',
                boxShadow: isScanning
                    ? '0 0 22px rgba(255,90,60,0.4), inset 0 1px 0 rgba(255,160,140,0.3), 4px 4px 0 rgba(0,0,0,0.7)'
                    : '0 0 22px rgba(232,177,90,0.5), inset 0 1px 0 rgba(255,220,160,0.3), 4px 4px 0 rgba(0,0,0,0.7)',
            }}
        >
            {!isScanning && <span><span style={{fontSize: 30, marginRight: 10, filter: 'grayscale(0.2)'}}>📷</span>Scan</span>}
            {isScanning && <span>Cancel</span>}
        </button>}
    </div>

    if (gameState['state'] === "redeemer") battleView = <div style={{
        flex: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
    }}>
        <div style={{fontSize: 30, marginBottom: 10, color: '#AA0000'}}>REDEEMER</div>
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: '256px',
            height: '256px',
            border: '12px solid #AA0000',
            backgroundColor: 'black',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center'
        }}>
            <QrReader
                onResult={(result, error) => {
                    if (!!result && result.text) {
                        setIsScanning(false);
                        setGameState({});
                        startBattle({opponentToken: result.text});
                    }

                    if (!!error) {
                        console.info(error);
                    }
                }}
                containerStyle={{width: '100%'}}
                constraints={{facingMode: 'environment'}}
            />
        </div>
        <div style={{fontSize: 30, marginTop: 10, color: '#AA0000'}}>Scan to Redeem</div>
    </div>

    if (gameState['state'] === "redeemable") battleView = <div style={{
        flex: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
    }}>
        <div>
            <div style={{fontSize: 30, marginBottom: 10, textAlign: 'center'}}>REDEMPTION</div>
            <div style={{background: 'white', border: '12px solid white'}}>
                <QRCode
                    style={{height: "auto", width: "100%"}}
                    value={gameState.token}
                    viewBox={`0 0 256 256`}
                />
            </div>
        </div>
    </div>


    // if (gameState['state'] === "start-battle") battleView = <>
    //     <div style={{height: "200", width: '200'}}>
    //         <div style={{background: 'white', padding: '16px'}}>
    //             <QRCode
    //                 style={{height: "auto", maxWidth: "100%", width: "100%"}}
    //                 value={gameState.token}
    //                 viewBox={`0 0 256 256`}
    //             />
    //         </div>
    //     </div>
    //     <div style={{position: 'relative'}}>
    //         <input type="text" className="inputbox" placeholder={'Token to Battle'} value={opponentToken}
    //                onChange={(event) => setOpponentToken(event.target.value)}/>
    //         <span className="focus-border"><i/></span>
    //     </div>
    //     <button
    //         style={{
    //             border: '1px white solid',
    //             padding: 10,
    //             margin: 30,
    //             fontWeight: 400,
    //             fontSize: 30,
    //             color: 'white',
    //             backgroundColor: 'black'
    //         }} onClick={() => startBattle({opponentToken})}>Battle
    //     </button>
    // </>

    const isCritical = gameState && gameState['critical'];
    const showTrilemmaBackdrop = gameState['state'] === 'in-battle'
        || gameState['state'] === 'start-battle'
        || gameState['state'] === 'redeemer'
        || gameState['state'] === 'redeemable'
        || isShowingLastTurn
        || isShowingBattleStart;
    return <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
    }}>
        {showTrilemmaBackdrop && <TrilemmaBackdrop darken={0.45}/>}
        <style>
            {`
          @keyframes enlarge {
            0% { transform: scale(100%); }
            100% { transform: scale(112%); }
          }
          @keyframes slideLeft {
            0% { transform: translateX(-10%); opacity: 0; }
            10% { opacity: 1; }
            100% { transform: translateX(-25%); }
          }
          @keyframes slideRight {
            0% { transform: translateX(10%); opacity: 0; }
            10% { opacity: 0; }
            20% { opacity: 1; }
            100% { transform: translateX(25%); }
          }
        `}
        </style>
        <div style={{
            position: 'absolute',
            pointerEvents: isShowingBattleStart ? 'auto' : 'none',
            width: '100%',
            height: '100%',
            opacity: isShowingBattleStart ? 1 : 0,
            transition: isShowingBattleStart ? 'none' : 'all 0.4s ease',
            overflow: 'hidden',
            isolation: 'isolate',
            color: 'white',
            zIndex: 200,
        }}>
            {isShowingBattleStart && <div style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.10)',
                pointerEvents: 'none',
            }}/>}
            {isShowingBattleStart && <div style={{
                width: '100%',
                animation: 'enlarge 8s linear infinite',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 14,
            }}>
                <div style={{
                    fontSize: 56,
                    fontWeight: 900,
                    letterSpacing: 4,
                    textTransform: 'uppercase',
                    color: '#f1d28b',
                    textShadow: '0 0 20px rgba(232,177,90,0.8), 3px 3px 0 rgba(0,0,0,0.95)',
                    fontStyle: 'italic',
                }}>Battle<br/>Start!</div>
                <div style={{
                    fontSize: 28,
                    fontWeight: 800,
                    letterSpacing: 6,
                    opacity: 0.7,
                    textShadow: '2px 2px 0 rgba(0,0,0,0.9)',
                }}>VS.</div>
                <div style={{textAlign: 'center'}}>
                    <img
                        src={gameState.opponent.image_url}
                        alt={gameState.opponent.name}
                        style={{display: 'block', width: 300, height: 300, objectFit: 'cover', borderRadius: 6, marginBottom: 10, marginInline: 'auto'}}
                    />
                    <div style={{fontSize: 36, fontWeight: 800, lineHeight: 1.1, textShadow: '2px 2px 0 rgba(0,0,0,0.9)'}}>{gameState.opponent.name}</div>
                    <div style={{fontSize: 20, opacity: 0.9, fontStyle: 'italic', textShadow: '1px 1px 0 rgba(0,0,0,0.9)'}}>{gameState.opponent.nickname}</div>
                </div>
            </div>}
        </div>
        <div style={{
            position: 'absolute',
            pointerEvents: isShowingLastTurn ? 'auto' : 'none',
            width: '100%',
            height: '100%',
            opacity: isShowingLastTurn ? 1 : 0,
            transition: isShowingLastTurn ? 'none' : 'all 0.4s ease',
            overflow: 'hidden',
            isolation: 'isolate',
            color: 'white',
            zIndex: 200,
        }}>
            {isShowingLastTurn && <div style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.18)',
                pointerEvents: 'none',
            }}/>}
            {isShowingLastTurn && <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 24,
            }}>
                <div style={{
                    animation: 'slideLeft 8s linear infinite',
                    display: 'flex',
                    alignItems: 'center',
                }}>
                    <img
                        src={gameState.player.image_url}
                        alt={gameState.player.nickname}
                        style={{
                            display: 'block',
                            width: 200,
                            height: 200,
                            objectFit: 'cover',
                            willChange: 'filter',
                            filter: 'drop-shadow(0 0 1px black) drop-shadow(0 0 1px black) drop-shadow(0 0 1px black)',
                            zIndex: 1,
                        }}
                    />
                    <div style={{textAlign: 'left', marginLeft: -56, zIndex: 2, position: 'relative'}}>
                        <div style={{
                            fontSize: 22,
                            fontWeight: 800,
                            letterSpacing: 2,
                            textTransform: 'uppercase',
                            opacity: 0.7,
                            textShadow: '2px 2px 0 rgba(0,0,0,0.9)',
                            marginLeft: 40,
                        }}>You used</div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                        }}>
                            <img
                                src={`/trilemma-assets/${moveIconKey(gameState.lastTurn.playerMove)}.png`}
                                alt={gameState.lastTurn.playerMove}
                                style={{
                                    width: 64,
                                    height: 64,
                                    objectFit: 'contain',
                                    filter: `drop-shadow(0 0 10px ${getColor(gameState.lastTurn.playerMove)}) drop-shadow(0 0 20px ${getColor(gameState.lastTurn.playerMove)}cc)`,
                                }}
                            />
                            <div style={{
                                fontSize: 44,
                                fontWeight: 900,
                                fontStyle: 'italic',
                                color: getColor(gameState.lastTurn.playerMove),
                                textShadow: `0 0 16px ${getColor(gameState.lastTurn.playerMove)}cc, 3px 3px 0 rgba(0,0,0,0.95)`,
                            }}>{gameState.lastTurn.playerMove}</div>
                        </div>
                    </div>
                </div>

                <div style={{
                    animation: 'slideRight 8s linear infinite',
                    display: 'flex',
                    alignItems: 'center',
                }}>
                    <div style={{textAlign: 'right', marginRight: -56, zIndex: 2, position: 'relative'}}>
                        <div style={{
                            fontSize: 22,
                            fontWeight: 800,
                            letterSpacing: 2,
                            textTransform: 'uppercase',
                            opacity: 0.7,
                            textShadow: '2px 2px 0 rgba(0,0,0,0.9)',
                            marginRight: 40,
                        }}>{gameState.opponent.name} used</div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            justifyContent: 'flex-start',
                        }}>
                            <img
                                src={`/trilemma-assets/${moveIconKey(gameState.lastTurn.opponentMove)}.png`}
                                alt={gameState.lastTurn.opponentMove}
                                style={{
                                    width: 64,
                                    height: 64,
                                    objectFit: 'contain',
                                    filter: `drop-shadow(0 0 10px ${getColor(gameState.lastTurn.opponentMove)}) drop-shadow(0 0 20px ${getColor(gameState.lastTurn.opponentMove)}cc)`,
                                }}
                            />
                            <div style={{
                                fontSize: 44,
                                fontWeight: 900,
                                fontStyle: 'italic',
                                color: getColor(gameState.lastTurn.opponentMove),
                                textShadow: `0 0 16px ${getColor(gameState.lastTurn.opponentMove)}cc, 3px 3px 0 rgba(0,0,0,0.95)`,
                            }}>{gameState.lastTurn.opponentMove}</div>
                        </div>
                    </div>
                    <img
                        src={gameState.opponent.image_url}
                        alt={gameState.opponent.nickname}
                        style={{
                            display: 'block',
                            width: 200,
                            height: 200,
                            objectFit: 'cover',
                            willChange: 'filter',
                            filter: 'drop-shadow(0 0 1px black) drop-shadow(0 0 1px black) drop-shadow(0 0 1px black)',
                        }}
                    />
                </div>
            </div>}
        </div>

        {!isShowingBattleStart && !isShowingLastTurn && <div style={{
            width: '100%',
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            boxSizing: 'border-box',
        }}>
            {isCritical && gameState['state'] !== 'in-battle' && <PulsingCriticalComponent isCritical={isCritical}/>}
            {battleView}
        </div>}

        {isCritical && !isShowingBattleStart && !isShowingLastTurn && <div style={{
            position: 'fixed',
            inset: 0,
            background: 'radial-gradient(ellipse at center, rgba(128,0,0,0) 50%, rgba(170,0,0,0.4) 100%)',
            pointerEvents: 'none',
            zIndex: 100,
        }}/>}
    </div>;
}

const PulsingCriticalComponent = () => {
    return (
        <div
            className="digital pulsing-critical"
            style={{
                marginTop: 12,
                fontSize: 16,
                padding: 6,
                color: '#FF0000',
                fontWeight: 'bold',
                fontStyle: 'italic',
                borderRadius: '8px',
                border: '1px solid black',
                display: 'inline-block',
                transition: 'all 500ms',
                animation: 'pulse 2s infinite',
            }}
        >
            CRITICAL
            <style>
                {`
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.4);
              color: #FF1111;
            }
            70% {
              box-shadow: 0 0 0 10px rgba(255, 0, 0, 0);
              color: #950000;
            }
            100% {
              box-shadow: 0 0 0 0 rgba(255, 0, 0, 0);
              color: #FF1111;
            }
          }
        `}
            </style>
        </div>
    );
};

const moveIconKey = (move) => {
    switch (move.toLowerCase()) {
        case 'attack': return 'sword';
        case 'reflect': return 'mirror';
        case 'heal': return 'cross';
        default: return 'sword';
    }
};

const MoveButton = ({move, performMove, selected, hidden}) => {
    const getGradient = (moveType) => {
        switch (moveType.toLowerCase()) {
            case 'attack':
                return 'linear-gradient(335deg, #ff5a5a, #e60000)';
            case 'reflect':
                return 'linear-gradient(335deg, #99e5ff, #00a3cc)';
            case 'heal':
                return 'linear-gradient(335deg, #4ddb60, #00b300)';
            default:
                return 'linear-gradient(335deg, #f5f5f5, #d9d9d9)';
        }
    };


    const getDescription = (moveType) => {
        switch (moveType.toLowerCase()) {
            case 'attack':
                return 'Deals damage to Heal';
            case 'reflect':
                return <span>Deals damage to Attack,<br/>takes damage from Heal</span>;
            case 'heal':
                return 'Recovers from Critical State';
            default:
                return '';
        }
    };

    const accent = getColor(move);
    return (
        <button
            onClick={() => performMove({move: move.toUpperCase()})}
            style={{
                opacity: hidden ? 0 : 1,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
                width: '100%',
                padding: '14px 24px',
                border: 'none',
                background: 'transparent',
                color: selected ? 'white' : accent,
                fontSize: 32,
                fontWeight: 900,
                fontFamily: 'inherit',
                cursor: 'pointer',
                textShadow: `0 0 14px ${accent}cc, 2px 2px 0 rgba(0,0,0,0.95)`,
                transition: 'all 0.2s ease',
            }}
        >
      <img
        src={`/trilemma-assets/${moveIconKey(move)}.png`}
        alt={move}
        style={{
            width: 88,
            height: 88,
            marginRight: 14,
            objectFit: 'contain',
            flexShrink: 0,
            filter: `drop-shadow(0 0 8px ${accent}) drop-shadow(0 0 16px ${accent}aa)`,
            transform: selected ? 'scale(1.1)' : 'scale(1)',
            transition: 'transform 0.2s ease',
        }}
      />
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left'}}>
                <span style={{textTransform: 'uppercase'}}>{move}</span>
                <span
                    style={{
                        fontSize: '15px',
                        fontWeight: 600,
                        lineHeight: 1.25,
                        opacity: 0.95,
                    }}
                >
          {getDescription(move)}
        </span>
            </div>
        </button>
    );
};